/**
 * AdminLlmSlotsPage — admin-only read-only audit panel showing each
 * LLM-powered feature's current provider/model/key routing.
 *
 * Why this page exists:
 *   API keys never live in Mongo or in the app DB — they live in
 *   `.env` (set via Emergent's secret UI). This page provides a
 *   complementary audit view so admins can:
 *     1. Confirm a freshly-pasted env key is taking effect (no
 *        need to inspect server-side logs or write curl).
 *     2. See at a glance which features are still on the legacy
 *        EMERGENT_LLM_KEY fallback vs. their own dedicated key.
 *     3. Get the EXACT env-var names to copy into Emergent's
 *        secret UI when configuring a new slot.
 *
 * Never displays the API keys themselves — only metadata. Even an
 * admin can't read the configured key value back from the UI.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Sparkles, KeyRound, CheckCircle2, AlertTriangle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePageHeader } from '../context/PageHeaderContext';

export default function AdminLlmSlotsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageHeader({ title: 'LLM Routing' }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/llm/slots');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load slot status');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`Copied: ${text}`));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#e8ede6] via-[#dfe7dc] to-[#d4ddd1] dark:from-[#1a1a1a] dark:to-[#0a0a0a]">
      <div className="max-w-3xl mx-auto px-4 pt-6 pb-12">
        <p className="text-xs text-muted-foreground mb-6 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#586D52]" />
          Per-feature model + provider audit. Keys are configured in the Emergent secrets UI.
        </p>

        {loading ? (
          <p className="text-center text-xs text-muted-foreground py-12">Loading…</p>
        ) : !data ? (
          <p className="text-center text-xs text-red-600 py-12">Failed to load.</p>
        ) : (
          <div className="space-y-4">
            {/* Fallback key status — visible up top so admins know if the
                "everything works by default" universal key is present. */}
            <Card data-testid="llm-fallback-status">
              <CardContent className="p-4 flex items-center gap-3">
                {data.emergent_universal_key_present ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">Emergent Universal Key — present</p>
                      <p className="text-[11px] text-muted-foreground">
                        Slots without a dedicated key automatically fall back to this.
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">EMERGENT_LLM_KEY</Badge>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">Emergent Universal Key — missing</p>
                      <p className="text-[11px] text-muted-foreground">
                        Slots without their own key will fail. Set EMERGENT_LLM_KEY OR set every slot's API key.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* One card per slot */}
            {(data.slots || []).map((s) => (
              <Card key={s.slot} data-testid={`llm-slot-${s.slot.toLowerCase()}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <KeyRound className="w-4 h-4 text-[#586D52]" />
                        <h3 className="text-sm font-bold">{s.label}</h3>
                        {s.configured ? (
                          s.using_fallback_key ? (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800">
                              Using fallback key
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-800">
                              Dedicated key
                            </Badge>
                          )
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-red-50 border-red-300 text-red-800">
                            Not configured
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
                        {s.description}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div className="bg-white/60 dark:bg-[#2a2a2a] rounded-md px-3 py-2">
                      <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Provider</p>
                      <p className="font-mono font-medium mt-0.5">{s.provider}</p>
                      <p className="text-[9px] text-muted-foreground mt-0.5">default: {s.default_provider}</p>
                    </div>
                    <div className="bg-white/60 dark:bg-[#2a2a2a] rounded-md px-3 py-2">
                      <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Model</p>
                      <p className="font-mono font-medium mt-0.5 truncate">{s.model}</p>
                      <p className="text-[9px] text-muted-foreground mt-0.5 truncate">default: {s.default_model}</p>
                    </div>
                  </div>

                  {/* Env var copy-helpers so admins can grab the exact
                      names to paste into Emergent's secrets UI. */}
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                      Show env var names (3)
                    </summary>
                    <div className="mt-2 space-y-1.5">
                      {[
                        { name: s.env_var_api_key, hint: 'Your provider API key (e.g., sk-...). Required to override fallback.' },
                        { name: s.env_var_provider, hint: 'Provider override. Defaults to "' + s.default_provider + '".' },
                        { name: s.env_var_model, hint: 'Model override. Defaults to "' + s.default_model + '".' },
                      ].map((row) => (
                        <div key={row.name} className="flex items-center gap-2 bg-gray-50 dark:bg-[#222] rounded px-2 py-1.5">
                          <code className="flex-1 font-mono text-[11px] text-gray-700 dark:text-gray-200">{row.name}</code>
                          <button
                            onClick={() => copy(row.name)}
                            className="text-gray-500 hover:text-gray-900 p-1"
                            title="Copy env var name"
                            data-testid={`llm-copy-${row.name}`}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <p className="text-[10px] text-muted-foreground italic mt-1.5">
                        Paste these names into the Emergent secrets UI. Changes take effect after the next backend restart.
                      </p>
                    </div>
                  </details>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
